import { useCallback, useState } from 'react';

import { useSnackbar } from 'notistack';
import { $chatCallbacks } from 'src/components/ai/chat/chat-state';
import type { PinnedSkill } from 'src/components/ai/chat/ChatPanel/ChatEditor/ChatEditorSkill/utils';
import { Apollo } from 'src/gql/generated';
import { useGlobalDrawer } from 'src/layouts/GlobalRightDrawer';

import { getEditorSkill, getSkillEditorKey } from './skill-editor-draft';
import { getErrorMessage, getOutdatedSkillError, SKILLS_LIST_REFETCH } from './skill-editor-errors';
import { getIsOutdatedSkillVersionError } from './skill-save-error';
import type { SkillDraftSession } from './use-skill-draft-session';

/** What the author does with a draft: test it in chat, publish it, or discard it. */
export function useSkillDraftPublishing({ session }: { session: SkillDraftSession }) {
  const {
    flushEditors,
    getHasUnsavedChanges,
    initializeEditor,
    initializedKeyRef,
    reportSaveError,
    requestSave,
    saveErrorRef,
    skillRef,
    writeSkill,
  } = session;
  const { enqueueSnackbar } = useSnackbar();
  const { openDrawer } = useGlobalDrawer();
  const [discardOpen, setDiscardOpen] = useState(false);
  const [publishConfirmOpen, setPublishConfirmOpen] = useState(false);
  const [testDraft] = Apollo.useTestAgentSkillDraftMutation({ errorPolicy: 'none' });
  const [publishDraft, { loading: publishing }] = Apollo.usePublishAgentSkillDraftMutation({
    errorPolicy: 'none',
  });
  const [discardDraft, { loading: discarding }] = Apollo.useDiscardAgentSkillDraftMutation({
    errorPolicy: 'none',
  });

  /** Accept the skill a draft mutation returns without the editor reloading it as a remote change. */
  const markLoaded = useCallback(
    (updated: Apollo.AgentSkillSimpleFragment | undefined) => {
      if (updated) initializedKeyRef.current = getSkillEditorKey(updated);
    },
    [initializedKeyRef]
  );

  const pinInChat = useCallback(
    (pinnedSkill: PinnedSkill) => {
      openDrawer();
      if (!$chatCallbacks.insertSkill?.(pinnedSkill)) $chatCallbacks.pendingSkill = pinnedSkill;
    },
    [openDrawer]
  );
  /**
   * The version a chat should pin: the editor's draft through an unlisted test version, or,
   * with nothing unpublished and for everyone else, the published version. An unfinished
   * draft still pins the published version when one exists. Null when there is no version
   * to pin, or the test version could not be made, which it reports itself.
   */
  const resolvePinnedSkill = useCallback(
    async ({ preferPublished }: { preferPublished: boolean }): Promise<PinnedSkill | null> => {
      const current = skillRef.current;
      if (!current) return null;
      const published = current.currentVersion;
      const pinPublished = preferPublished || !current.draft || !current.hasUnpublishedChanges;
      if (published && pinPublished)
        return {
          description: published.description,
          name: published.name,
          skillId: current.id,
          versionId: published.id,
        };
      if (!current.isComplete || !current.draft) return null;

      try {
        const result = await testDraft({
          variables: { input: { expectedRevision: current.draft.revision, id: current.id } },
        });
        const version = result.data?.testAgentSkillDraft.version;
        if (!version) throw new Error('The server did not return the test version.');
        return {
          description: version.description,
          isTestVersion: !version.publishedAt,
          name: version.name,
          skillId: current.id,
          versionId: version.id,
        };
      } catch (error) {
        enqueueSnackbar(getErrorMessage(error, 'Could not open this skill in chat.'), {
          variant: 'error',
        });
        return null;
      }
    },
    [enqueueSnackbar, skillRef, testDraft]
  );

  const performTest = useCallback(async () => {
    const pinnedSkill = await resolvePinnedSkill({ preferPublished: false });
    if (pinnedSkill) pinInChat(pinnedSkill);
  }, [pinInChat, resolvePinnedSkill]);

  const performPublish = useCallback(
    async (confirmed = false) => {
      const current = skillRef.current;
      if (!current?.draft) return;
      if (!confirmed) {
        setPublishConfirmOpen(true);
        return;
      }

      try {
        const result = await publishDraft({
          refetchQueries: SKILLS_LIST_REFETCH,
          update: (_cache, { data }) => markLoaded(data?.publishAgentSkillDraft.agentSkill),
          variables: {
            input: {
              expectedRevision: current.draft.revision,
              id: current.id,
            },
          },
        });
        const updated = result.data?.publishAgentSkillDraft.agentSkill;
        if (!updated) throw new Error('The server did not confirm the publish.');
        writeSkill(getEditorSkill(updated));
        enqueueSnackbar('Skill published.', { variant: 'success' });
      } catch (error) {
        if (getIsOutdatedSkillVersionError(error))
          reportSaveError(getOutdatedSkillError('publish'));
        else
          enqueueSnackbar(getErrorMessage(error, 'Could not publish this skill.'), {
            variant: 'error',
          });
      }
    },
    [enqueueSnackbar, markLoaded, publishDraft, reportSaveError, skillRef, writeSkill]
  );
  const closePublishConfirm = useCallback(() => setPublishConfirmOpen(false), []);
  const confirmPublish = useCallback(() => {
    setPublishConfirmOpen(false);
    void performPublish(true);
  }, [performPublish]);

  const openDiscard = useCallback(() => setDiscardOpen(true), []);
  const closeDiscard = useCallback(() => setDiscardOpen(false), []);
  const discardChanges = useCallback(async () => {
    setDiscardOpen(false);
    // Edits still waiting are saved first, so the revision discarded is the latest one.
    flushEditors();
    await requestSave();
    const current = skillRef.current;
    if (!current?.draft || saveErrorRef.current || getHasUnsavedChanges()) return;
    try {
      const result = await discardDraft({
        refetchQueries: SKILLS_LIST_REFETCH,
        update: (_cache, { data }) => markLoaded(data?.discardAgentSkillDraft.agentSkill),
        variables: { input: { expectedRevision: current.draft.revision, id: current.id } },
      });
      const updated = result.data?.discardAgentSkillDraft.agentSkill;
      if (!updated) throw new Error('The server did not confirm the discard.');
      initializeEditor(updated);
    } catch (error) {
      if (getIsOutdatedSkillVersionError(error)) reportSaveError(getOutdatedSkillError('discard'));
      else
        enqueueSnackbar('Could not discard the changes. Please try again.', { variant: 'error' });
    }
  }, [
    discardDraft,
    enqueueSnackbar,
    flushEditors,
    getHasUnsavedChanges,
    initializeEditor,
    markLoaded,
    reportSaveError,
    requestSave,
    saveErrorRef,
    skillRef,
  ]);

  return {
    closeDiscard,
    closePublishConfirm,
    confirmPublish,
    discardChanges,
    discardOpen,
    discarding,
    openDiscard,
    performPublish,
    performTest,
    publishConfirmOpen,
    publishing,
    resolvePinnedSkill,
  };
}
